/**
 * Web-specific PartnerEarningsDashboard
 * Uses CSS-based charts instead of victory-native/react-native-skia
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  useWindowDimensions,
  FlatList,
  TextInput,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  getPartnerSplitEarnings,
  PartnerSplitEarnings,
} from '../services/revenue-split-service';
import { getAllPartnerChargerTransactionDetails } from '../services/partner-service';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '../navigation/stackTypes';
import { HeaderWithMenu } from '../components/menu';

type NavigationProp = StackNavigationProp<RootStackParamList>;

// Web-compatible Bar Chart using CSS
interface BarChartData {
  x: number;
  meander: number;
  split: number;
  total: number;
  label: string;
}

interface WebBarChartProps {
  data: BarChartData[];
  hasSeparateEarnings: boolean;
  showMeanderLegend: boolean;
  showSplitLegend: boolean;
}

const WebBarChart: React.FC<WebBarChartProps> = ({
  data,
  hasSeparateEarnings,
  showMeanderLegend,
  showSplitLegend,
}) => {
  const maxValue = Math.max(
    ...data.map((d) => Math.max(d.meander + d.split, d.total, 1))
  );

  return (
    <View style={chartStyles.container}>
      <View style={chartStyles.barsContainer}>
        {data.map((item, index) => {
          const totalHeight = hasSeparateEarnings
            ? ((item.meander + item.split) / maxValue) * 100
            : (item.total / maxValue) * 100;

          return (
            <View key={index} style={chartStyles.barWrapper}>
              <View style={chartStyles.barColumn}>
                {hasSeparateEarnings ? (
                  <View style={[chartStyles.stackedBar, { height: `${totalHeight}%` }]}>
                    {showSplitLegend && item.split > 0 && (
                      <View
                        style={[
                          chartStyles.barSegment,
                          {
                            backgroundColor: '#51B868',
                            height: `${(item.split / (item.meander + item.split)) * 100}%`,
                          },
                        ]}
                      />
                    )}
                    {showMeanderLegend && item.meander > 0 && (
                      <View
                        style={[
                          chartStyles.barSegment,
                          {
                            backgroundColor: '#3B82F6',
                            height: `${(item.meander / (item.meander + item.split)) * 100}%`,
                          },
                        ]}
                      />
                    )}
                  </View>
                ) : (
                  <View
                    style={[
                      chartStyles.bar,
                      {
                        height: `${totalHeight}%`,
                        backgroundColor: '#51B868',
                      },
                    ]}
                  />
                )}
              </View>
              <Text style={chartStyles.label}>{item.label}</Text>
            </View>
          );
        })}
      </View>

      {/* Legend */}
      {hasSeparateEarnings && (showMeanderLegend || showSplitLegend) && (
        <View style={chartStyles.legend}>
          {showMeanderLegend && (
            <View style={chartStyles.legendItem}>
              <View style={[chartStyles.legendDot, { backgroundColor: '#3B82F6' }]} />
              <Text style={chartStyles.legendText}>Meander Partner</Text>
            </View>
          )}
          {showSplitLegend && (
            <View style={chartStyles.legendItem}>
              <View style={[chartStyles.legendDot, { backgroundColor: '#51B868' }]} />
              <Text style={chartStyles.legendText}>Revenue Split</Text>
            </View>
          )}
        </View>
      )}
    </View>
  );
};

const chartStyles = StyleSheet.create({
  container: {
    padding: 16,
  },
  barsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    height: 180,
    paddingBottom: 24,
  },
  barWrapper: {
    flex: 1,
    alignItems: 'center',
    height: '100%',
  },
  barColumn: {
    flex: 1,
    width: '100%',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingHorizontal: 2,
  },
  stackedBar: {
    width: 16,
    borderRadius: 4,
    overflow: 'hidden',
    justifyContent: 'flex-end',
  },
  barSegment: {
    width: '100%',
  },
  bar: {
    width: 16,
    borderRadius: 4,
    minHeight: 4,
  },
  label: {
    color: '#8D8E90',
    fontSize: 10,
    marginTop: 4,
  },
  legend: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 16,
    marginTop: 12,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  legendDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  legendText: {
    color: '#E8E8E9',
    fontSize: 12,
  },
});

// Web-compatible Pie Chart using CSS
interface PieChartData {
  label: string;
  value: number;
  color: string;
}

interface WebPieChartProps {
  data: PieChartData[];
}

const WebPieChart: React.FC<WebPieChartProps> = ({ data }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  
  if (total === 0) {
    return (
      <View style={pieStyles.container}>
        <View style={pieStyles.emptyPie}>
          <Text style={pieStyles.emptyText}>No data</Text>
        </View>
      </View>
    );
  }

  // Create gradient string for conic gradient
  let currentAngle = 0;
  const gradientParts: string[] = [];
  
  data.forEach((item) => {
    if (item.value > 0) {
      const percentage = (item.value / total) * 100;
      const endAngle = currentAngle + percentage;
      gradientParts.push(`${item.color} ${currentAngle}% ${endAngle}%`);
      currentAngle = endAngle;
    }
  });

  return (
    <View style={pieStyles.container}>
      <View style={pieStyles.chartWrapper}>
        <View
          style={[
            pieStyles.pie,
            {
              // @ts-ignore - web-specific style
              background: `conic-gradient(${gradientParts.join(', ')})`,
            },
          ]}
        >
          <View style={pieStyles.innerCircle}>
            <Text style={pieStyles.totalLabel}>Total</Text>
            <Text style={pieStyles.totalValue}>${total.toFixed(2)}</Text>
          </View>
        </View>
      </View>
      
      {/* Legend */}
      <View style={pieStyles.legend}>
        {data.slice(0, 5).map((item, index) => (
          <View key={index} style={pieStyles.legendItem}>
            <View style={[pieStyles.legendDot, { backgroundColor: item.color }]} />
            <Text style={pieStyles.legendText} numberOfLines={1}>
              {item.label}
            </Text>
          </View>
        ))}
        {data.length > 5 && (
          <Text style={pieStyles.moreText}>+{data.length - 5} more</Text>
        )}
      </View>
    </View>
  );
};

const pieStyles = StyleSheet.create({
  container: {
    alignItems: 'center',
    padding: 16,
  },
  chartWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  pie: {
    width: 200,
    height: 200,
    borderRadius: 100,
    alignItems: 'center',
    justifyContent: 'center',
  },
  innerCircle: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: '#1A1D21',
    alignItems: 'center',
    justifyContent: 'center',
  },
  totalLabel: {
    color: '#8D8E90',
    fontSize: 12,
  },
  totalValue: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '700',
    marginTop: 4,
  },
  emptyPie: {
    width: 200,
    height: 200,
    borderRadius: 100,
    backgroundColor: '#292D33',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    color: '#8D8E90',
    fontSize: 14,
  },
  legend: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 12,
    marginTop: 16,
    maxWidth: 300,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  legendText: {
    color: '#E8E8E9',
    fontSize: 11,
    maxWidth: 80,
  },
  moreText: {
    color: '#8D8E90',
    fontSize: 11,
    fontStyle: 'italic',
  },
});

const PartnerEarningsDashboard = () => {
  const navigation = useNavigation<NavigationProp>();
  const { width } = useWindowDimensions();
  const userSession = useSelector((state: RootState) => state.user.session);
  const [earnings, setEarnings] = useState<PartnerSplitEarnings | null>(null);
  const [earningsTrend, setEarningsTrend] = useState<Array<{
    month: string;
    meanderPartnerEarnings: number;
    revenueSplitEarnings: number;
    totalEarnings: number;
    orderCount: number;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter chargers based on search query
  const filteredChargers = useMemo(() => {
    if (!earnings?.byCharger) return [];
    if (!searchQuery.trim()) {
      return earnings.byCharger;
    }
    const query = searchQuery.toLowerCase();
    return earnings.byCharger.filter(charger => 
      charger.chargerName.toLowerCase().includes(query) ||
      charger.hostUsername?.toLowerCase().includes(query) ||
      charger.chargerId.toLowerCase().includes(query)
    );
  }, [earnings?.byCharger, searchQuery]);

  // Prepare pie chart data for top 10 chargers by monthly earnings
  const topChargers = useMemo(() => {
    if (!earnings?.byCharger) return [];
    return [...earnings.byCharger]
      .sort((a, b) => b.monthlyEarnings - a.monthlyEarnings)
      .slice(0, 10);
  }, [earnings?.byCharger]);

  const pieData = useMemo(() => {
    return topChargers.map((charger, index) => ({
      label: charger.chargerName.length > 15 ? charger.chargerName.substring(0, 15) + '...' : charger.chargerName,
      value: charger.monthlyEarnings,
      color: `hsl(${(index * 360) / Math.max(topChargers.length, 1)}, 70%, 60%)`,
    }));
  }, [topChargers]);

  useEffect(() => {
    fetchEarnings();
  }, []);

  const fetchEarnings = async () => {
    console.log('[PartnerEarningsDashboard.web] fetchEarnings called');
    
    if (!userSession?.id) {
      console.log('[PartnerEarningsDashboard.web] No user session id, returning early');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch earnings data
      const [earningsResponse, transactionResponse] = await Promise.all([
        getPartnerSplitEarnings(userSession.id, userSession.token),
        getAllPartnerChargerTransactionDetails(userSession.token),
      ]);

      console.log('[PartnerEarningsDashboard.web] earningsResponse:', JSON.stringify(earningsResponse, null, 2));

      if (earningsResponse.status === 200 && earningsResponse.payload) {
        setEarnings(earningsResponse.payload);
      }

      // Process transaction data for monthly trend
      if (transactionResponse.status === 200 && transactionResponse.payload?.chargers) {
        const chargers = transactionResponse.payload.chargers;
        
        // Aggregate monthly data across all chargers
        const monthlyMap = new Map<string, {
          meanderPartnerEarnings: number;
          revenueSplitEarnings: number;
          totalEarnings: number;
          orderCount: number;
        }>();

        // Get last 12 months
        const now = new Date();
        for (let i = 11; i >= 0; i--) {
          const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const monthKey = date.toISOString().slice(0, 7);
          monthlyMap.set(monthKey, {
            meanderPartnerEarnings: 0,
            revenueSplitEarnings: 0,
            totalEarnings: 0,
            orderCount: 0,
          });
        }

        // Process each charger's history
        chargers.forEach((chargerData: any) => {
          const chargerHistory = chargerData.chargerHistory || [];
          const isSplitCharger = chargerData.permissions?.includes('split');

          chargerHistory.forEach((transaction: any) => {
            if (transaction.charging_start_time) {
              const transactionDate = new Date(transaction.charging_start_time);
              const monthKey = transactionDate.toISOString().slice(0, 7);
              
              if (monthlyMap.has(monthKey)) {
                const current = monthlyMap.get(monthKey)!;
                const earnings = transaction.total_kwh || 0;
                
                if (isSplitCharger) {
                  current.revenueSplitEarnings += earnings;
                } else {
                  current.meanderPartnerEarnings += earnings;
                }
                current.totalEarnings += earnings;
                current.orderCount += 1;
                monthlyMap.set(monthKey, current);
              }
            }
          });
        });

        const trendData = Array.from(monthlyMap.entries())
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([month, data]) => ({
            month,
            ...data,
          }));

        setEarningsTrend(trendData);
      }
    } catch (err) {
      console.error('[PartnerEarningsDashboard.web] Error fetching earnings:', err);
      setError('Failed to load earnings data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <View style={{ flex: 1, backgroundColor: '#1A1D21' }}>
        <SafeAreaView style={styles.container} edges={['top']}>
          <HeaderWithMenu forceHamburger title="Earnings Dashboard" insideSafeArea />
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#3B82F6" />
            <Text style={styles.loadingText}>Loading earnings...</Text>
          </View>
        </SafeAreaView>
      </View>
    );
  }

  if (error) {
    return (
      <View style={{ flex: 1, backgroundColor: '#1A1D21' }}>
        <SafeAreaView style={styles.container} edges={['top']}>
          <HeaderWithMenu forceHamburger title="Earnings Dashboard" insideSafeArea />
          <View style={styles.errorContainer}>
            <Ionicons name="warning-outline" size={64} color="#F97777" />
            <Text style={styles.errorText}>{error}</Text>
            <TouchableOpacity style={styles.retryButton} onPress={fetchEarnings}>
              <Text style={styles.retryButtonText}>Retry</Text>
            </TouchableOpacity>
          </View>
        </SafeAreaView>
      </View>
    );
  }

  if (!earnings) {
    return (
      <View style={{ flex: 1, backgroundColor: '#1A1D21' }}>
        <SafeAreaView style={styles.container} edges={['top']}>
          <HeaderWithMenu forceHamburger title="Earnings Dashboard" insideSafeArea />
          <View style={styles.emptyState}>
            <Ionicons name="cash-outline" size={64} color="#8D8E90" />
            <Text style={styles.emptyStateTitle}>No Earnings Data</Text>
            <Text style={styles.emptyStateText}>
              You haven't earned any revenue splits yet
            </Text>
          </View>
        </SafeAreaView>
      </View>
    );
  }

  const { summary, byCharger, monthlyTrend } = earnings;

  // Prepare bar chart data
  const barData = earningsTrend.length > 0
    ? earningsTrend.map((month, index) => {
        const date = new Date(month.month + '-01');
        const monthName = date.toLocaleDateString('en-US', { month: 'short' });
        return {
          x: index,
          meander: month.meanderPartnerEarnings,
          split: month.revenueSplitEarnings,
          total: month.totalEarnings,
          label: monthName,
        };
      })
    : (monthlyTrend && monthlyTrend.length > 0)
      ? monthlyTrend.map((month, index) => {
          const date = new Date(month.month + '-01');
          const monthName = date.toLocaleDateString('en-US', { month: 'short' });
          return {
            x: index,
            meander: 0,
            split: 0,
            total: month.earnings,
            label: monthName,
          };
        })
      : [];

  const hasSeparateEarnings = earningsTrend.length > 0 &&
    earningsTrend.some(t => t.meanderPartnerEarnings > 0 || t.revenueSplitEarnings > 0);

  const totalMeanderEarnings = earningsTrend.reduce((sum, t) => sum + (t.meanderPartnerEarnings || 0), 0);
  const totalSplitEarnings = earningsTrend.reduce((sum, t) => sum + (t.revenueSplitEarnings || 0), 0);
  const showMeanderLegend = totalMeanderEarnings > 0;
  const showSplitLegend = totalSplitEarnings > 0;
  const hasAnyNonZeroValues = barData.some(d => d.meander !== 0 || d.split !== 0 || d.total !== 0);

  const cardWidth = Math.min((width - 44) / 2, 180);

  return (
    <View style={{ flex: 1, backgroundColor: '#1A1D21' }}>
      <SafeAreaView style={{ flex: 1 }} edges={['top']}>
        <HeaderWithMenu forceHamburger title="Earnings Dashboard" insideSafeArea />
        <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
          {/* Summary Cards */}
          <View style={styles.summaryCardsContainer}>
            <View style={[styles.summaryCard, { width: cardWidth }]}>
              <View style={styles.summaryCardHeader}>
                <Ionicons name="cash-outline" size={20} color="#51B868" />
                <Text style={styles.summaryCardLabel}>This Month</Text>
              </View>
              <Text style={[styles.summaryCardValue, { color: '#51B868' }]}>
                ${summary.monthlyEarnings.toFixed(2)}
              </Text>
            </View>

            <View style={[styles.summaryCard, { width: cardWidth }]}>
              <View style={styles.summaryCardHeader}>
                <Ionicons name="trending-up-outline" size={20} color="#3B82F6" />
                <Text style={styles.summaryCardLabel}>Total Earned</Text>
              </View>
              <Text style={styles.summaryCardValue}>
                ${summary.totalEarnings.toFixed(2)}
              </Text>
            </View>

            <View style={[styles.summaryCard, { width: cardWidth }]}>
              <View style={styles.summaryCardHeader}>
                <Ionicons name="time-outline" size={20} color="#F59E0B" />
                <Text style={styles.summaryCardLabel}>Pending</Text>
              </View>
              <Text style={styles.summaryCardValue}>
                ${summary.totalPending.toFixed(2)}
              </Text>
            </View>

            <View style={[styles.summaryCard, { width: cardWidth }]}>
              <View style={styles.summaryCardHeader}>
                <Ionicons name="git-network-outline" size={20} color="#3B82F6" />
                <Text style={styles.summaryCardLabel}>Chargers</Text>
              </View>
              <Text style={styles.summaryCardValue}>{summary.chargerCount}</Text>
            </View>
          </View>

          {/* Payout Information */}
          <View style={styles.payoutSection}>
            <View style={styles.payoutHeader}>
              <Text style={styles.sectionTitle}>Payout Information</Text>
            </View>
            <View style={styles.payoutDetails}>
              <View style={styles.payoutRow}>
                <View style={styles.payoutLabelWithIcon}>
                  <Ionicons name="checkmark-circle" size={16} color="#10B981" />
                  <Text style={styles.payoutLabel}>Total Paid:</Text>
                </View>
                <Text style={[styles.payoutValue, { color: '#10B981', fontWeight: '700' }]}>
                  ${summary.totalPaid.toFixed(2)}
                </Text>
              </View>
              <View style={styles.payoutRow}>
                <View style={styles.payoutLabelWithIcon}>
                  <Ionicons name="time-outline" size={16} color="#F59E0B" />
                  <Text style={styles.payoutLabel}>Pending:</Text>
                </View>
                <Text style={[styles.payoutValue, { color: '#F59E0B', fontWeight: '700' }]}>
                  ${summary.totalPending.toFixed(2)}
                </Text>
              </View>
              <View style={styles.payoutRow}>
                <View style={styles.payoutLabelWithIcon}>
                  <Ionicons name="lock-closed" size={16} color="#EF4444" />
                  <Text style={styles.payoutLabel}>Withheld:</Text>
                </View>
                <Text style={[styles.payoutValue, { color: '#EF4444', fontWeight: '700' }]}>
                  ${summary.totalWithheld.toFixed(2)}
                </Text>
              </View>
            </View>
          </View>

          {/* Earnings by Charger - Pie Chart */}
          {byCharger.length > 0 && (
            <View style={styles.chartSection}>
              <Text style={styles.chartTitle}>Earnings by Charger (This Month)</Text>
              {byCharger.length > 10 && (
                <Text style={styles.chartSubtitle}>Showing top 10 earners</Text>
              )}
              <WebPieChart data={pieData} />
            </View>
          )}

          {/* Charger Details */}
          <View style={styles.listSection}>
            <View style={styles.listHeader}>
              <Text style={styles.sectionTitle}>
                Chargers ({filteredChargers.length}{filteredChargers.length !== byCharger.length ? ` of ${byCharger.length}` : ''})
              </Text>
            </View>

            {/* Search Bar */}
            <View style={styles.searchContainer}>
              <Ionicons name="search" size={20} color="#8D8E90" style={styles.searchIcon} />
              <TextInput
                style={styles.searchInput}
                placeholder="Search by name, host, or ID..."
                placeholderTextColor="#8D8E90"
                value={searchQuery}
                onChangeText={setSearchQuery}
              />
              {searchQuery.length > 0 && (
                <TouchableOpacity
                  onPress={() => setSearchQuery('')}
                  style={styles.clearButton}
                >
                  <Ionicons name="close-circle" size={20} color="#8D8E90" />
                </TouchableOpacity>
              )}
            </View>

            {/* Charger List */}
            <FlatList
              data={filteredChargers}
              scrollEnabled={false}
              keyExtractor={(item) => item.chargerId}
              renderItem={({ item: charger, index }) => {
                const originalIndex = byCharger.findIndex(c => c.chargerId === charger.chargerId);
                const isTopCharger = topChargers.some(tc => tc.chargerId === charger.chargerId);
                const topChargerIndex = topChargers.findIndex(tc => tc.chargerId === charger.chargerId);
                const chargerColor = isTopCharger
                  ? pieData[topChargerIndex]?.color
                  : `hsl(${(originalIndex * 360) / byCharger.length}, 70%, 60%)`;

                return (
                  <View style={styles.chargerCard}>
                    <View
                      style={[
                        styles.colorIndicator,
                        { backgroundColor: chargerColor }
                      ]}
                    />
                    <View style={styles.chargerRank}>
                      <Text style={styles.rankText}>#{originalIndex + 1}</Text>
                    </View>
                    <View style={styles.chargerInfo}>
                      <Text style={styles.chargerName}>
                        {String(charger.chargerName || 'Unknown')}
                      </Text>
                      <Text style={styles.chargerHost}>
                        Host: {String(charger.hostUsername || 'Unknown')}
                      </Text>
                      <Text style={styles.chargerSplit}>
                        Split: {Number(charger.splitPercentage || 0).toFixed(1)}%
                      </Text>
                    </View>
                    <View style={styles.chargerEarnings}>
                      <Text style={styles.earningsAmount}>
                        ${Number(charger.monthlyEarnings || 0).toFixed(2)}
                      </Text>
                      <Text style={styles.earningsLabel}>this month</Text>
                      <Text style={styles.earningsTotal}>
                        ${Number(charger.totalEarnings || 0).toFixed(2)} total
                      </Text>
                    </View>
                  </View>
                );
              }}
              ListEmptyComponent={
                <View style={styles.emptySearchState}>
                  <Ionicons name="search-outline" size={48} color="#8D8E90" />
                  <Text style={styles.emptySearchText}>No chargers found</Text>
                  <Text style={styles.emptySearchSubtext}>Try a different search term</Text>
                </View>
              }
            />
          </View>

          {/* Monthly Earnings Trend */}
          <View style={styles.chartSection}>
            <Text style={styles.chartTitle}>Monthly Earnings Trend</Text>
            {barData.length === 0 ? (
              <View style={{ padding: 20, alignItems: 'center' }}>
                <Text style={{ color: '#8D8E90', fontSize: 14 }}>
                  No earnings trend data available
                </Text>
              </View>
            ) : !hasAnyNonZeroValues ? (
              <View style={{ padding: 20, alignItems: 'center' }}>
                <Text style={{ color: '#8D8E90', fontSize: 14 }}>
                  No earnings recorded in the last 12 months
                </Text>
              </View>
            ) : (
              <WebBarChart
                data={barData}
                hasSeparateEarnings={hasSeparateEarnings}
                showMeanderLegend={showMeanderLegend}
                showSplitLegend={showSplitLegend}
              />
            )}
          </View>
        </ScrollView>
      </SafeAreaView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#1A1D21',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: '#8D8E90',
    fontSize: 16,
    marginTop: 12,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 30,
  },
  errorText: {
    color: '#F97777',
    fontSize: 16,
    textAlign: 'center',
    marginTop: 16,
    marginBottom: 24,
  },
  retryButton: {
    backgroundColor: '#3B82F6',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  retryButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 30,
  },
  emptyStateTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '700',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyStateText: {
    color: '#8D8E90',
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
  },
  content: {
    flex: 1,
    padding: 16,
  },
  summaryCardsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 20,
    gap: 12,
    justifyContent: 'center',
  },
  summaryCard: {
    backgroundColor: '#292D33',
    borderRadius: 12,
    padding: 16,
    minHeight: 100,
  },
  summaryCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  summaryCardLabel: {
    color: '#E8E8E9',
    fontSize: 12,
    fontWeight: '500',
    flex: 1,
  },
  summaryCardValue: {
    color: '#3B82F6',
    fontSize: 24,
    fontWeight: '700',
  },
  payoutSection: {
    backgroundColor: '#292D33',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  payoutHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionTitle: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
  },
  payoutDetails: {
    backgroundColor: '#1A1D23',
    borderRadius: 8,
    padding: 12,
  },
  payoutRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 6,
  },
  payoutLabelWithIcon: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  payoutLabel: {
    color: '#8D8E90',
    fontSize: 14,
  },
  payoutValue: {
    color: '#E8E8E9',
    fontSize: 14,
    fontWeight: '600',
  },
  chartSection: {
    backgroundColor: '#292D33',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  chartTitle: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 16,
  },
  chartSubtitle: {
    color: '#8D8E90',
    fontSize: 12,
    marginBottom: 12,
    fontStyle: 'italic',
  },
  listSection: {
    marginBottom: 20,
  },
  listHeader: {
    marginBottom: 12,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#292D33',
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginBottom: 12,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    color: '#fff',
    fontSize: 14,
    paddingVertical: 8,
  },
  clearButton: {
    padding: 4,
  },
  chargerCard: {
    backgroundColor: '#292D33',
    borderRadius: 12,
    padding: 12,
    marginBottom: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  colorIndicator: {
    width: 4,
    height: '100%',
    borderRadius: 2,
    marginRight: 12,
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
  },
  chargerRank: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#51B868',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  rankText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '700',
  },
  chargerInfo: {
    flex: 1,
  },
  chargerName: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  chargerHost: {
    color: '#8D8E90',
    fontSize: 11,
    marginTop: 2,
  },
  chargerSplit: {
    color: '#3B82F6',
    fontSize: 11,
    marginTop: 2,
    fontWeight: '600',
  },
  chargerEarnings: {
    alignItems: 'flex-end',
    marginRight: 8,
  },
  earningsAmount: {
    color: '#51B868',
    fontSize: 16,
    fontWeight: '700',
  },
  earningsLabel: {
    color: '#8D8E90',
    fontSize: 10,
    marginTop: 2,
  },
  earningsTotal: {
    color: '#8D8E90',
    fontSize: 10,
    marginTop: 2,
  },
  emptySearchState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
    paddingHorizontal: 20,
  },
  emptySearchText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    marginTop: 16,
  },
  emptySearchSubtext: {
    color: '#8D8E90',
    fontSize: 14,
    marginTop: 8,
  },
});

export default PartnerEarningsDashboard;

